// ============================================================================
// EDUSPHERE ENTERPRISE - INDIAN SCHOOL CONTEXT
// Context provider for board-specific configurations
// ============================================================================

import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';
import {
  EducationBoard,
  BoardConfig,
  SubjectConfig,
  GradeConfig,
  HolidayConfig,
  FeeCategory,
  getBoardConfig,
  getSubjectsForBoard,
  getGradeForMarks,
  getPassingMarks,
  isPassingGrade,
  calculateCGPA,
  getHolidaysForBoard,
  getFeeCategories,
  TN_STATE_BOARD_CONFIG,
  TN_STATE_BOARD_SUBJECTS,
} from '@/config/indianSchoolConfig';

// ============================================================================
// CONTEXT TYPES
// ============================================================================

interface IndianSchoolContextType {
  // Current Board Selection
  currentBoard: EducationBoard;
  setCurrentBoard: (board: EducationBoard) => void;
  
  // Board Configuration
  boardConfig: BoardConfig;
  
  // State Selection (for state-specific features)
  currentState: string;
  setCurrentState: (state: string) => void;
  
  // Academic Year
  currentAcademicYear: string;
  setCurrentAcademicYear: (year: string) => void;
  
  // Medium of Instruction
  mediumOfInstruction: 'english' | 'tamil' | 'hindi' | 'bilingual';
  setMediumOfInstruction: (medium: 'english' | 'tamil' | 'hindi' | 'bilingual') => void;
  
  // Helper Functions
  getSubjects: (classNumber: number) => SubjectConfig[];
  getGrade: (marks: number) => GradeConfig | undefined;
  getPassMarks: () => number;
  isPassing: (marks: number) => boolean;
  getCGPA: (subjectMarks: number[]) => number;
  getHolidays: () => HolidayConfig[];
  getFees: (classNumber: number) => FeeCategory[];
  
  // Language Helpers
  getLocalizedText: (english: string, tamil?: string, hindi?: string) => string;
  
  // Board-specific Helpers
  isBoardExamClass: (classNumber: number) => boolean;
  getStreamOptions: () => string[];
  getExamPattern: () => string;
}

interface IndianSchoolProviderProps {
  children: ReactNode;
  defaultBoard?: EducationBoard;
  defaultState?: string;
}

// ============================================================================
// CREATE CONTEXT
// ============================================================================

const IndianSchoolContext = createContext<IndianSchoolContextType | undefined>(undefined);

// ============================================================================
// PROVIDER COMPONENT
// ============================================================================

export function IndianSchoolProvider({
  children,
  defaultBoard: _defaultBoard = 'TN_STATE_BOARD',
  defaultState = 'Tamil Nadu'
}: IndianSchoolProviderProps) {
  // State - board is always TN_STATE_BOARD
  const currentBoard: EducationBoard = 'TN_STATE_BOARD';
  const setCurrentBoard = (_board: EducationBoard) => { /* no-op: TN State Board only */ };
  const [currentState, setCurrentState] = useState<string>(defaultState);
  const [currentAcademicYear, setCurrentAcademicYear] = useState<string>('2024-25');
  const [mediumOfInstruction, setMediumOfInstruction] = useState<'english' | 'tamil' | 'hindi' | 'bilingual'>('english');
  
  // Derived State
  const boardConfig = useMemo(() => getBoardConfig(currentBoard), [currentBoard]);
  
  // Helper Functions
  const getSubjects = useCallback((classNumber: number): SubjectConfig[] => {
    return getSubjectsForBoard(currentBoard, classNumber);
  }, [currentBoard]);
  
  const getGrade = useCallback((marks: number): GradeConfig | undefined => {
    return getGradeForMarks(marks, currentBoard);
  }, [currentBoard]);
  
  const getPassMarks = useCallback((): number => {
    return getPassingMarks(currentBoard);
  }, [currentBoard]);
  
  const isPassing = useCallback((marks: number): boolean => {
    return isPassingGrade(marks, currentBoard);
  }, [currentBoard]);
  
  const getCGPA = useCallback((subjectMarks: number[]): number => {
    return calculateCGPA(subjectMarks, currentBoard);
  }, [currentBoard]);
  
  const getHolidays = useCallback((): HolidayConfig[] => {
    return getHolidaysForBoard(currentBoard, currentState);
  }, [currentBoard, currentState]);
  
  const getFees = useCallback((classNumber: number): FeeCategory[] => {
    return getFeeCategories(currentBoard, classNumber);
  }, [currentBoard]);
  
  const getLocalizedText = useCallback((
    english: string, 
    tamil?: string, 
    hindi?: string
  ): string => {
    switch (mediumOfInstruction) {
      case 'tamil':
        return tamil || english;
      case 'hindi':
        return hindi || english;
      case 'bilingual':
        if (currentState === 'Tamil Nadu' && tamil) {
          return `${english} / ${tamil}`;
        }
        if (hindi) {
          return `${english} / ${hindi}`;
        }
        return english;
      default:
        return english;
    }
  }, [mediumOfInstruction, currentState]);
  
  const isBoardExamClass = useCallback((classNumber: number): boolean => {
    return classNumber === 10 || classNumber === 12;
  }, []);
  
  const getStreamOptions = useCallback((): string[] => {
    return [
      'Science - Maths Group / அறிவியல் - கணிதப் பிரிவு',
      'Science - Biology Group / அறிவியல் - உயிரியல் பிரிவு',
      'Commerce Group / வணிகவியல் பிரிவு',
      'Arts Group / கலைப் பிரிவு',
      'Vocational Group / தொழிற்கல்வி பிரிவு'
    ];
  }, []);
  
  const getExamPattern = useCallback((): string => {
    return boardConfig.examPattern.name;
  }, [boardConfig]);
  
  // Context Value
  const contextValue: IndianSchoolContextType = useMemo(() => ({
    currentBoard,
    setCurrentBoard,
    boardConfig,
    currentState,
    setCurrentState,
    currentAcademicYear,
    setCurrentAcademicYear,
    mediumOfInstruction,
    setMediumOfInstruction,
    getSubjects,
    getGrade,
    getPassMarks,
    isPassing,
    getCGPA,
    getHolidays,
    getFees,
    getLocalizedText,
    isBoardExamClass,
    getStreamOptions,
    getExamPattern,
  }), [
    currentBoard,
    boardConfig,
    currentState,
    currentAcademicYear,
    mediumOfInstruction,
    getSubjects,
    getGrade,
    getPassMarks,
    isPassing,
    getCGPA,
    getHolidays,
    getFees,
    getLocalizedText,
    isBoardExamClass,
    getStreamOptions,
    getExamPattern,
  ]);
  
  return (
    <IndianSchoolContext.Provider value={contextValue}>
      {children}
    </IndianSchoolContext.Provider>
  );
}

// ============================================================================
// CUSTOM HOOK
// ============================================================================

export function useIndianSchool(): IndianSchoolContextType {
  const context = useContext(IndianSchoolContext);
  if (context === undefined) {
    throw new Error('useIndianSchool must be used within an IndianSchoolProvider');
  }
  return context;
}

// ============================================================================
// BOARD SELECTOR COMPONENT
// ============================================================================

interface BoardSelectorProps {
  showLabel?: boolean;
  size?: 'small' | 'medium';
  variant?: 'outlined' | 'filled' | 'standard';
  onChange?: (board: EducationBoard) => void;
}

/** @deprecated Board selection is no longer needed - TN State Board only. Renders nothing. */
export function BoardSelector(_props: BoardSelectorProps) {
  return null;
}

// ============================================================================
// GRADE DISPLAY COMPONENT
// ============================================================================

interface GradeDisplayProps {
  marks: number;
  maxMarks?: number;
  showGradePoint?: boolean;
  showRemarks?: boolean;
}

export function GradeDisplay({ 
  marks, 
  maxMarks = 100,
  showGradePoint = false,
  showRemarks = false 
}: GradeDisplayProps) {
  const { getGrade, isPassing, getLocalizedText } = useIndianSchool();
  
  const percentage = (marks / maxMarks) * 100;
  const gradeInfo = getGrade(percentage);
  const passed = isPassing(percentage);
  
  if (!gradeInfo) {
    return <span>-</span>;
  }
  
  return (
    <div style={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: '8px',
      padding: '4px 8px',
      borderRadius: '4px',
      backgroundColor: gradeInfo.color,
      color: '#fff',
    }}>
      <span style={{ fontWeight: 'bold' }}>{gradeInfo.grade}</span>
      {showGradePoint && (
        <span style={{ fontSize: '0.85em' }}>({gradeInfo.gradePoint})</span>
      )}
      {showRemarks && (
        <span style={{ fontSize: '0.85em' }}>
          {getLocalizedText(gradeInfo.remarks, gradeInfo.remarksTamil)}
        </span>
      )}
    </div>
  );
}

// ============================================================================
// PASSING STATUS COMPONENT
// ============================================================================

interface PassingStatusProps {
  marks: number;
  maxMarks?: number;
  showLabel?: boolean;
}

export function PassingStatus({ marks, maxMarks = 100, showLabel = true }: PassingStatusProps) {
  const { isPassing, getPassMarks, getLocalizedText } = useIndianSchool();
  
  const percentage = (marks / maxMarks) * 100;
  const passed = isPassing(percentage);
  const passMarks = getPassMarks();
  
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      color: passed ? '#2e7d32' : '#d32f2f',
    }}>
      {passed ? '✓' : '✗'}
      {showLabel && (
        <span>
          {passed 
            ? getLocalizedText('Pass', 'தேர்ச்சி', 'पास')
            : getLocalizedText('Fail', 'தோல்வி', 'फेल')
          }
        </span>
      )}
      <span style={{ fontSize: '0.85em', color: '#666' }}>
        (Min: {passMarks}%)
      </span>
    </div>
  );
}

// ============================================================================
// UTILITY EXPORTS
// ============================================================================

export {
  TN_STATE_BOARD_CONFIG,
  TN_STATE_BOARD_SUBJECTS,
  getBoardConfig,
  getSubjectsForBoard,
  getGradeForMarks,
  getPassingMarks,
  isPassingGrade,
  calculateCGPA,
  getHolidaysForBoard,
  getFeeCategories,
};

export type { EducationBoard, BoardConfig, SubjectConfig, GradeConfig };
